import { useEffect, useMemo, useState } from 'react'
import {
	MapPin,
	Trash2,
	Camera,
	Plus,
	Edit,
	ChevronRight,
	Star,
	Clock,
	Check,
	Upload,
	Package,
	X,
	Save,
	XCircle,
	ShoppingBag,
	Download,
	Calendar,
	Truck,
	RefreshCw,
	CheckCircle,
	StarIcon,
	ChevronUp,
	ChevronDown,
	AlertCircle,
	AlertTriangle,
	Info,
	FileText,
	CreditCard,
	DollarSign,
	Receipt,
	Percent,
	Tag,
	CalendarFold,
} from 'lucide-react'
import InputGroupContent from '../elements/input'
import DirectionCard from '../cards/direction-card'
import CustomersEntity, {
	CustomersAddressEntity,
} from '@/entities/api/customers/customer'
import Toast from '../elements/toast'
import { Link } from 'react-router-dom'
import useUploadProgress from '@/hooks/useUploadProgress'
import { useImageUploadToast } from '@/hooks/useImageUploadToast'
import { useUploader } from '@/hooks/useUploader'
import {
	createCustomerAddress,
	createCustomerImage,
	createProductRaiting,
	deleteCustomerAddress,
	deleteCustomerImage,
	updateCustomer,
	updateCustomerAddress,
	verifyProductRaiting,
} from '@/services/api/customers/customer'
import getInitials from '@/utils/getInitials'
import { API_URL } from '@/services/api/keys'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@/store/store'
import { updateUserData } from '@/store/features/auth'
import Select from '../elements/select'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import addressSchema from '@/entities/schemas/address'
import { ToastConfirm } from '../elements/toast-confirm'
import {
	UserUpdateFormData,
	userUpdateSchema,
} from '@/entities/schemas/customer'
import { RatingFormData, ratingSchema } from '@/entities/schemas/raiting'
import OrdersEntity, {
	OrderStatus,
	ShippingInfo,
	ShippingType,
} from '@/entities/api/order/order'
import { fetchOrdersByCustomer } from '@/services/api/order/order'
import { TypesCoupon } from '@/entities/api/coupons/coupon'
import { Entities } from '@/entities/api/entities'
import formatCurrency from '@/utils/price-convert'
import municipiosData from '@/utils/muncipios.json'



type FormMode = 'view' | 'create' | 'edit'
type AddressFormData = yup.InferType<typeof addressSchema>
export const AccountDirections = ({
	customer,
	token,
}: {
	customer: CustomersEntity | null
	token: string | null
}) => {
	const dispatch = useDispatch()
	const [formMode, setFormMode] = useState<FormMode>('view')
	const [editingAddressId, setEditingAddressId] = useState<string | undefined>(
		undefined
	)
	const [addresSelect, setAddresSelect] =
		useState<CustomersAddressEntity | null>()
	const [selectedAddressId, setSelectedAddressId] = useState<
		string | undefined
	>(undefined)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [selectedDepartment, setSelectedDepartment] = useState<string>('')

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
		setValue,
		watch,
	} = useForm<AddressFormData>({
		resolver: yupResolver(addressSchema),
		defaultValues: {
			fullname: '',
			document_type: 'CC',
			document: 0,
			province: '',
			city: '',
			neighborhood: '',
			street_type: 'Calle',
			street: '',
			number_home: '',
			type_home: 'Casa',
			phone: 0,
			additional_references: '',
			postal_code: '',
		},
	})

	const watchProvince = watch('province')

	const departmentOptions = useMemo(() => {
		return municipiosData.departamentos.map((dept) => ({
			label: dept.nombre,
			value: dept.nombre,
		}))
	}, [])

	const cityOptions = useMemo(() => {
		if (!watchProvince) return []

		const selectedDept = municipiosData.departamentos.find(
			(dept) => dept.nombre === watchProvince
		)

		if (!selectedDept) return []

		if (!selectedDept.municipios || selectedDept.municipios.length === 0) {
			return [{
				label: selectedDept.nombre,
				value: selectedDept.nombre,
			}]
		}

		return selectedDept.municipios.map((mun) => ({
			label: mun.nombre,
			value: mun.nombre,
		}))
	}, [watchProvince])

	const isDepartmentWithoutMunicipios = useMemo(() => {
		if (!watchProvince) return false

		const selectedDept = municipiosData.departamentos.find(
			(dept) => dept.nombre === watchProvince
		)

		return selectedDept && (!selectedDept.municipios || selectedDept.municipios.length === 0)
	}, [watchProvince])

	useEffect(() => {
		if (watchProvince !== selectedDepartment) {
			if (isDepartmentWithoutMunicipios) {
				setValue('city', watchProvince)
			} else {
				setValue('city', '')
			}
			setSelectedDepartment(watchProvince)
		}
	}, [watchProvince, selectedDepartment, setValue, isDepartmentWithoutMunicipios])

	const addresses = customer?.address?.filter((addr) => !addr._deleted) || []
	const hasAddresses = addresses.length > 0

	const resetForm = () => {
		reset({
			fullname: '',
			document_type: 'CC',
			document: 0,
			province: '',
			city: '',
			neighborhood: '',
			street_type: 'Calle',
			street: '',
			number_home: '',
			type_home: 'Casa',
			phone: 0,
			additional_references: '',
			postal_code: '',
		})
		setFormMode('view')
		setEditingAddressId(undefined)
		setSelectedDepartment('')
	}

	const handleAddAddress = () => {
		resetForm()
		setFormMode('create')
	}

	const handleEditAddress = (addressId: string) => {
		const addressToEdit = addresses.find((addr) => addr._id === addressId)
		if (addressToEdit) {
			reset({
				fullname: addressToEdit.fullname,
				document_type: addressToEdit.document_type,
				document: addressToEdit.document,
				province: addressToEdit.province,
				city: addressToEdit.city,
				neighborhood: addressToEdit.neighborhood,
				street_type: addressToEdit.street_type,
				street: addressToEdit.street,
				number_home: addressToEdit.number_home,
				type_home: addressToEdit.type_home,
				phone: addressToEdit.phone,
				additional_references: addressToEdit.additional_references || '',
				postal_code: addressToEdit.postal_code,
			})
			setEditingAddressId(addressId)
			setAddresSelect(addressToEdit)
			setSelectedDepartment(addressToEdit.province)
			setFormMode('edit')
		}
	}

	const handleDeleteAddress = async (addressId: string) => {
		if (!customer?._id || !token) {
			Toast({ type: 'error', message: 'Datos de sesión no disponibles' })
			return
		}

		ToastConfirm({
			message: '¿Estás seguro de que deseas eliminar esta dirección?',
			onConfirm: async () => {
				try {
					setIsSubmitting(true)
					const response = await deleteCustomerAddress(
						customer._id ?? '',
						addressId,
						token
					)

					if (response?.status === 200 || response?.status === 204) {
						const updatedAddresses =
							customer.address?.filter((addr) => addr._id !== addressId) || []

						dispatch(
							updateUserData({
								customer: {
									...customer,
									address: updatedAddresses,
								},
							})
						)

						Toast({
							type: 'success',
							message: 'Dirección eliminada exitosamente',
						})

						if (selectedAddressId === addressId) {
							setSelectedAddressId(undefined)
						}
					} else {
						throw new Error(
							response?.data?.message || 'Error al eliminar la dirección'
						)
					}
				} catch (error) {
					console.error('Error al eliminar dirección:', error)
					Toast({
						type: 'error',
						message: 'Error al eliminar la dirección. Inténtalo nuevamente.',
					})
				} finally {
					setIsSubmitting(false)
				}
			},
			onCancel: () => {
				Toast({ type: 'info', message: 'Se cancelo la accion' })
			},
		})
	}

	const handleCancelForm = () => {
		resetForm()
	}

	const onSubmit = async (data: AddressFormData) => {
		if (!customer?._id || !token) {
			Toast({ type: 'error', message: 'Datos de sesión no disponibles' })
			return
		}

		try {
			setIsSubmitting(true)
			let response

			if (formMode === 'create') {
				if (customer?.address && customer?.address.length >= 5) {
					Toast({
						type: 'error',
						message: 'Solo se pueden agregar 5 direccíones por usuario',
					})
					setIsSubmitting(false)
					return
				}
				response = await createCustomerAddress(customer._id, data, token)
			} else if (formMode === 'edit' && editingAddressId) {
				response = await updateCustomerAddress(
					customer._id,
					editingAddressId,
					data,
					token
				)
			}

			if (response?.status === 200 || response?.status === 201) {
				if (response?.data?.data) {
					const newAddress =
						formMode === 'edit' && editingAddressId
							? { ...response.data.data, _id: editingAddressId }
							: response.data.data

					const existingAddresses = customer.address || []
					const updatedAddresses = existingAddresses.some(
						(addr) => addr._id === newAddress._id
					)
						? existingAddresses.map((addr) =>
								addr._id === newAddress._id ? newAddress : addr
						  )
						: [...existingAddresses, newAddress]

					dispatch(
						updateUserData({
							customer: {
								...customer,
								address: updatedAddresses,
							},
						})
					)
				}

				const successMessage =
					formMode === 'create'
						? 'Dirección creada exitosamente'
						: 'Dirección actualizada exitosamente'

				Toast({ type: 'success', message: successMessage })
				resetForm()
			} else {
				throw new Error(response?.data?.message || 'Error en la operación')
			}
		} catch (error) {
			console.error('Error al procesar dirección:', error)
			const errorMessage =
				formMode === 'create'
					? 'Error al crear la dirección. Inténtalo nuevamente.'
					: 'Error al actualizar la dirección. Inténtalo nuevamente.'
			Toast({ type: 'error', message: errorMessage })
		} finally {
			setIsSubmitting(false)
		}
	}

	return (
		<div className='w-full max-w-6xl mx-auto space-y-4 px-3 sm:px-4 lg:px-6'>
			<div className='bg-white rounded-lg border border-gray-200 p-4'>
				<div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
					<div className='flex items-center gap-3'>
						<div className='w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center'>
							<MapPin className='w-5 h-5 text-red-600' />
						</div>
						<div>
							<h2 className='text-lg font-semibold text-gray-900'>
								{formMode === 'create'
									? 'Nueva Dirección'
									: formMode === 'edit'
									? 'Editar Dirección'
									: 'Mis Direcciones'}
							</h2>
							<p className='text-sm text-gray-600'>
								{formMode === 'view'
									? hasAddresses
										? `${addresses.length} dirección${
												addresses.length !== 1 ? 'es' : ''
										  } registrada${addresses.length !== 1 ? 's' : ''}`
										: 'No tienes direcciones registradas'
									: 'Completa todos los campos obligatorios'}
							</p>
						</div>
					</div>
					{formMode === 'view' && (
						<button
							onClick={handleAddAddress}
							className='inline-flex items-center justify-center px-4 sm:px-6 py-2.5 sm:py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-all duration-300 hover:scale-105 shadow-md hover:shadow-lg text-sm sm:text-base whitespace-nowrap'
						>
							<Plus className='w-4 h-4 mr-2 flex-shrink-0' />
							Nueva dirección
						</button>
					)}
				</div>
			</div>

			{(formMode === 'create' || formMode === 'edit') && (
				<div className='bg-white p-6'>
					<form onSubmit={handleSubmit(onSubmit)} className='space-y-8'>
						<div className='space-y-6'>
							<h3 className='text-lg font-medium text-gray-900 pb-2'>
								Información Personal
							</h3>
							<div className='grid grid-cols-1 sm:grid-cols-2 gap-6'>
								<InputGroupContent
									label='Nombre completo'
									placeholder='Nombre para entregas'
									type='text'
									name='fullname'
									register={register}
									errors={errors}
									required={true}
								/>
								<Select
									name='document_type'
									label='Tipo de documento'
									placeholder='Selecciona el tipo de documento'
									options={[
										{ label: 'Cédula de Ciudadanía', value: 'CC' },
										{ label: 'Cédula de Extranjería', value: 'CE' },
										{ label: 'NIT', value: 'NIT' },
										{ label: 'Pasaporte', value: 'PP' },
									]}
									defaultValue={{
										label: addresSelect?.document_type ?? '',
										value: addresSelect?.document_type ?? '',
									}}
									register={register}
									errors={errors}
									isRequired={true}
								/>
							</div>
							<InputGroupContent
								label='Número de documento'
								placeholder='12345678'
								type='number'
								name='document'
								register={register}
								errors={errors}
								required={true}
							/>
						</div>

						<div className='space-y-6'>
							<h3 className='text-lg font-medium text-gray-900 border-b border-gray-200 pb-2'>
								Ubicación
							</h3>
							<div className='grid grid-cols-1 sm:grid-cols-2 gap-6'>
								<Select
									name='province'
									label='Departamento'
									placeholder='Selecciona el departamento'
									options={departmentOptions}
									defaultValue={{
										label: addresSelect?.province ?? '',
										value: addresSelect?.province ?? '',
									}}
									register={register}
									errors={errors}
									isRequired={true}
								/>
								<Select
									name='city'
									label='Ciudad'
									placeholder={
										watchProvince
											? 'Selecciona la ciudad'
											: 'Primero selecciona un departamento'
									}
									options={cityOptions}
									defaultValue={{
										label: addresSelect?.city ?? '',
										value: addresSelect?.city ?? '',
									}}
									register={register}
									errors={errors}
									isRequired={true}
									isDisabled={!watchProvince || isDepartmentWithoutMunicipios}
								/>
							</div>
							<InputGroupContent
								label='Barrio'
								placeholder='Nombre del barrio'
								type='text'
								name='neighborhood'
								register={register}
								errors={errors}
								required={true}
							/>
						</div>

						<div className='space-y-6'>
							<h3 className='text-lg font-medium text-gray-900 border-b border-gray-200 pb-2'>
								Dirección Detallada
							</h3>
							<div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
								<Select
									name='street_type'
									label='Tipo de vía'
									placeholder='Selecciona el tipo de vía'
									options={[
										{ label: 'Calle', value: 'Calle' },
										{ label: 'Carrera', value: 'Carrera' },
										{ label: 'Avenida', value: 'Avenida' },
										{ label: 'Diagonal', value: 'Diagonal' },
										{ label: 'Transversal', value: 'Transversal' },
									]}
									defaultValue={{
										label: addresSelect?.street_type ?? '',
										value: addresSelect?.street_type ?? '',
									}}
									register={register}
									errors={errors}
									isRequired={true}
								/>
								<InputGroupContent
									label='Nombre de vía'
									placeholder='Ej: 15'
									type='text'
									name='street'
									register={register}
									errors={errors}
									required={true}
								/>
								<InputGroupContent
									label='Número'
									placeholder='Ej: 25-10'
									type='text'
									name='number_home'
									register={register}
									errors={errors}
									required={true}
								/>
							</div>

							<div className='grid grid-cols-1 sm:grid-cols-2 gap-6'>
								<Select
									name='type_home'
									label='Tipo de vivienda'
									placeholder='Selecciona el tipo de vivienda'
									options={[
										{ label: 'Casa', value: 'Casa' },
										{ label: 'Apartamento', value: 'Apartamento' },
										{ label: 'Oficina', value: 'Oficina' },
										{ label: 'Local Comercial', value: 'Local' },
									]}
									defaultValue={{
										label: addresSelect?.type_home ?? '',
										value: addresSelect?.type_home ?? '',
									}}
									register={register}
									errors={errors}
									isRequired={true}
								/>
								<InputGroupContent
									label='Teléfono'
									placeholder='Teléfono de contacto'
									type='number'
									name='phone'
									register={register}
									errors={errors}
									required={true}
								/>
							</div>

							<InputGroupContent
								label='Código postal'
								placeholder='Ej: 660003'
								type='text'
								name='postal_code'
								register={register}
								errors={errors}
								required={true}
							/>

							<InputGroupContent
								label='Referencias adicionales (opcional)'
								placeholder='Casa blanca, portón negro, frente al parque...'
								type='text'
								name='additional_references'
								register={register}
								errors={errors}
								required={false}
							/>
						</div>

						<div className='flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200'>
							<button
								type='submit'
								disabled={isSubmitting}
								className='inline-flex items-center justify-center px-6 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-all duration-300 hover:scale-105 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100'
							>
								{isSubmitting ? (
									<>
										<div className='w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin' />
										Guardando...
									</>
								) : (
									<>
										<Save className='w-4 h-4 mr-2' />
										{formMode === 'create'
											? 'Crear dirección'
											: 'Guardar cambios'}
									</>
								)}
							</button>
							<button
								type='button'
								onClick={handleCancelForm}
								disabled={isSubmitting}
								className='inline-flex items-center justify-center px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed'
							>
								<X className='w-4 h-4 mr-2' />
								Cancelar
							</button>
						</div>
					</form>
				</div>
			)}

			{formMode === 'view' && (
				<>
					{!hasAddresses ? (
						<div className='bg-white p-8 text-center'>
							<div className='w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center'>
								<Package className='w-8 h-8 text-gray-400' />
							</div>
							<h3 className='text-lg font-medium text-gray-900 mb-2'>
								Sin direcciones de envío
							</h3>
							<p className='text-gray-600 mb-6 max-w-md mx-auto'>
								Agrega tu primera dirección de envío para recibir tus productos
								de SportCenter Shop.
							</p>
							<button
								onClick={handleAddAddress}
								className='inline-flex items-center px-6 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-all duration-300 hover:scale-105 shadow-md hover:shadow-lg'
							>
								<Plus className='w-4 h-4 mr-2' />
								Agregar primera dirección
							</button>
						</div>
					) : (
						<div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-4 mt-4 mb-4'>
							{addresses.map((address) => (
								<DirectionCard
									key={address._id}
									customerId={customer?._id || ''}
									address={address}
									token={token}
									onEdit={
										address._id
											? () => handleEditAddress(address._id!)
											: undefined
									}
									onDelete={
										address._id
											? () => handleDeleteAddress(address._id!)
											: undefined
									}
								/>
							))}
						</div>
					)}

					{hasAddresses && (
						<div className='bg-gray-50 rounded-lg border border-gray-200 p-4'>
							<div className='flex items-start gap-3'>
								<div className='w-6 h-6 flex items-center justify-center mt-0.5'>
									<MapPin className='w-4 h-4 text-gray-400' />
								</div>
								<div className='flex-1'>
									<h4 className='text-sm font-medium text-gray-900 mb-1'>
										Información importante
									</h4>
									<p className='text-sm text-gray-600 leading-relaxed'>
										Las direcciones son utilizadas para el envío de tus pedidos.
										Asegúrate de que la información esté completa y actualizada
										para garantizar una entrega exitosa.
									</p>
								</div>
							</div>
						</div>
					)}
				</>
			)}
		</div>
	)
}

export const AccountInformation = ({
	customer,
	token,
}: {
	customer: CustomersEntity | null
	token: string | null
}) => {
	const [isUploadImage, setIsUploadImage] = useState<boolean>(false)
	const [isUpdateImage, setIsUpdateImage] = useState<boolean>(false)
	const { inputRef, files, isProcessing, handleFileChange, clearFiles } =
		useUploader()
	const { handleProgress } = useImageUploadToast()
	const dispatch = useDispatch<AppDispatch>()
	const { executeWithProgress, progress, isLoadingProgress } =
		useUploadProgress(async () => {
			try {
				const first = files[0]
				if (!first) {
					Toast({ type: 'error', message: 'No hay archivos para subir' })
					return
				}

				if (customer?.images) {
					if (customer?.images[0]) {
						const response = await deleteCustomerImage(
							customer?._id ?? '',
							customer?.images[0]?._id ?? '',
							token ?? ''
						)

						if ([400, 401, 404].includes(response?.status ?? 0)) {
							Toast({
								type: 'error',
								message: 'No se pudo actualizar la imagen',
							})
							return
						}

						dispatch(updateUserData({ customer: { images: [] } }))
					}
				}

				const fileToUpload = first.file
				const response = await createCustomerImage(
					customer?._id ?? '',
					fileToUpload,
					token ?? ''
				)

				if (
					response?.status === 500 ||
					response?.status === 404 ||
					response?.status === 400
				) {
					Toast({
						type: 'error',
						message: 'La imagen no se puede actualizar.',
					})
					return
				}

				dispatch(updateUserData({ customer: { images: [response?.data] } }))
				setIsUploadImage(false)
				setIsUpdateImage(false)
				clearFiles()
			} catch (error) {
				console.log(error)
				Toast({
					type: 'error',
					message: 'Algo salio mal al actualizar al usuario',
				})
			}
		})
	const deleteAccount = () => {
		Toast({
			type: 'info',
			message:
				'Funcion no disponible por ahora, si quieres eliminar tu cuenta contacta al soporte',
		})
	}

	useEffect(() => {
		if (files.length > 0) {
			setIsUploadImage(true)
			Toast({
				type: 'success',
				duration: 1000,
				message: 'La imagen se cargo correctamente',
			})
		}
	}, [files])

	useEffect(() => {
		handleProgress(progress, isLoadingProgress)
	}, [progress, isLoadingProgress])

	return (
		<div className='w-full max-w-6xl mx-auto space-y-3 sm:space-y-4 px-2 sm:px-4 lg:px-6'>
			<div className='bg-white rounded-xl sm:rounded-2xl lg:rounded-3xl border border-gray-100 p-4 sm:p-6 lg:p-8 overflow-hidden relative'>
				<div className='absolute top-0 right-0 w-20 sm:w-32 h-20 sm:h-32 bg-gradient-to-br from-red-500/8 to-transparent rounded-full transform translate-x-8 sm:translate-x-16 -translate-y-8 sm:-translate-y-16'></div>
				<div className='relative'>
					<div className='flex flex-col items-center sm:flex-row  space-y-4 sm:space-y-0 sm:space-x-4 lg:space-x-6'>
						<div className='relative group flex-shrink-0 flex flex-col items-center sm:items-start'>
							<div className='w-28 h-28 sm:w-24 sm:h-24 lg:w-32 lg:h-32 rounded-2xl lg:rounded-3xl shadow-lg bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden'>
								{customer?.images && customer?.images[0] ? (
									<img
										src={`${API_URL}/${customer?.images[0]?.url}`}
										className='w-full h-full object-cover'
										alt='Profile'
									/>
								) : (
									<div className='w-full h-full rounded-2xl lg:rounded-3xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center ring-2 ring-red-500/50'>
										<span className='text-white font-semibold text-lg sm:text-base lg:text-xl'>
											{getInitials(
												customer?.name ?? '',
												customer?.lastname ?? ''
											)}
										</span>
									</div>
								)}
								<div className='absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300'></div>
							</div>

							<div
								className={`absolute -bottom-2 -right-2 sm:-bottom-1 sm:-right-1 lg:-bottom-2 lg:-right-2 ${
									isUpdateImage
										? isUploadImage
											? 'w-20 h-7 sm:w-24 sm:h-8 lg:w-28 lg:h-10 rounded-lg lg:rounded-xl'
											: 'w-16 h-7 sm:w-20 sm:h-8 lg:w-24 lg:h-10 rounded-lg lg:rounded-xl'
										: 'w-8 h-8 sm:w-7 sm:h-7 lg:w-10 lg:h-10 rounded-lg lg:rounded-xl'
								} bg-white shadow-lg border-2 border-gray-100 flex items-center justify-center hover:bg-red-50 hover:border-red-200 transition-all duration-300 group-hover:scale-110`}
							>
								{!isUpdateImage && (
									<button
										className='cursor-pointer'
										onClick={() => setIsUpdateImage(true)}
									>
										<Camera className='w-4 h-4 sm:w-3 sm:h-3 lg:w-5 lg:h-5 text-gray-600 hover:text-red-600 transition-colors' />
									</button>
								)}
								{isUpdateImage && !isProcessing && !isUploadImage && (
									<div className='relative flex items-center cursor-pointer'>
										<div className='absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full'></div>
										<div className='relative'>
											<input
												className='absolute inset-0 w-full h-full opacity-0 cursor-pointer'
												type='file'
												ref={inputRef}
												onChange={handleFileChange}
												accept='image/*'
											/>
											<Upload className='w-4 h-4 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-gray-600 hover:text-red-600 transition-colors' />
										</div>
									</div>
								)}
								{isProcessing && (
									<div className='animate-spin rounded-full h-4 w-4 sm:h-4 sm:w-4 lg:h-5 lg:w-5 border-b-2 border-red-600'></div>
								)}
								{isUpdateImage && isUploadImage && (
									<button
										onClick={() => executeWithProgress()}
										className='flex items-center space-x-1 px-1 sm:px-2'
									>
										<Check className='w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-green-600' />
										<span className='text-xs font-medium text-green-600 hidden sm:inline'>
											Guardar
										</span>
									</button>
								)}
							</div>
						</div>
						<div className='flex-1 min-w-0 space-y-2 text-center sm:text-left'>
							<h3 className='text-2xl sm:text-xl lg:text-3xl font-bold text-gray-900 truncate'>
								{`${customer?.name} ${customer?.lastname}`}
							</h3>
							<p className='text-base sm:text-sm lg:text-lg text-gray-600 break-all'>
								{customer?.email}
							</p>
							<div className='flex flex-col sm:flex-row items-center sm:items-start lg:items-center space-y-3 sm:space-y-0 sm:space-x-4 pt-2 sm:pt-1'>
								<Link
									to='/Perfil/configuracion'
									className='inline-flex items-center px-5 py-2.5 sm:px-4 sm:py-2 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-all duration-300 hover:scale-105 shadow-md hover:shadow-lg text-sm'
								>
									<Edit className='w-4 h-4 mr-2' />
									Editar perfil
								</Link>
								<div className='flex items-center space-x-2 text-green-600'>
									<div className='w-2 h-2 bg-green-500 rounded-full animate-pulse'></div>
									<span className='text-sm sm:text-xs lg:text-sm font-medium'>
										Verificado
									</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className='bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-100'>
				<div className='space-y-3'>
					<button className='w-full group relative overflow-hidden rounded-xl lg:rounded-2xl border border-gray-200 p-4 lg:p-5 hover:border-red-200 hover:bg-red-50/50 transition-all duration-300'>
						<Link
							to='/Perfil/direcciones'
							className='flex items-center justify-between'
						>
							<div className='flex items-center space-x-3 lg:space-x-4 min-w-0 flex-1'>
								<div className='w-10 h-10 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-xl lg:rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md group-hover:shadow-lg transition-all duration-300 flex-shrink-0'>
									<MapPin className='w-5 h-5 lg:w-6 lg:h-6 text-white' />
								</div>
								<div className='text-left min-w-0 flex-1'>
									<h5 className='text-base lg:text-lg font-bold text-gray-900 group-hover:text-red-600 transition-colors truncate'>
										Direcciones de envío
									</h5>
									<p className='text-gray-600 text-sm sm:text-xs lg:text-base truncate'>
										Gestiona tus direcciones de entrega
									</p>
								</div>
							</div>
							<ChevronRight className='w-5 h-5 text-gray-400 group-hover:text-red-600 group-hover:translate-x-1 transition-all duration-300 flex-shrink-0' />
						</Link>
					</button>

					<button
						onClick={deleteAccount}
						className='w-full group cursor-pointer relative overflow-hidden rounded-xl lg:rounded-2xl border border-red-200 p-4 lg:p-5 bg-red-50/50 hover:bg-red-100/70 transition-all duration-300'
					>
						<div className='flex items-center justify-between'>
							<div className='flex items-center space-x-3 lg:space-x-4 min-w-0 flex-1'>
								<div className='w-10 h-10 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-xl lg:rounded-2xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-md flex-shrink-0'>
									<Trash2 className='w-5 h-5 lg:w-6 lg:h-6 text-white' />
								</div>
								<div className='text-left min-w-0 flex-1'>
									<h5 className='text-base lg:text-lg font-bold text-red-600 truncate'>
										Eliminar cuenta
									</h5>
									<p className='text-red-500 text-sm sm:text-xs lg:text-base truncate'>
										Esta acción no se puede deshacer
									</p>
								</div>
							</div>
							<ChevronRight className='w-5 h-5 text-red-500 group-hover:translate-x-1 transition-all duration-300 flex-shrink-0' />
						</div>
					</button>
				</div>
			</div>
		</div>
	)
}

export const AccountFormUpdateData = ({
	customer,
	token,
}: {
	customer: CustomersEntity | null
	token: string | null
}) => {
	const dispatch = useDispatch()

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
	} = useForm<UserUpdateFormData>({
		resolver: yupResolver(userUpdateSchema),
		defaultValues: {
			name: customer?.name || '',
			lastname: customer?.lastname || '',
		},
	})

	const onSubmit = async (data: UserUpdateFormData) => {
		if (!customer?._id || !token) return

		try {
			const response = await updateCustomer(customer._id, data, token)

			if ([400, 401, 404, 409].includes(response?.status ?? 0)) {
				Toast({ type: 'error', message: 'Error actualizando el usuario' })
				return
			}

			if (response && response.status >= 200 && response.status < 300) {
				dispatch(
					updateUserData({
						customer: {
							name: data.name,
							lastname: data.lastname,
						},
					})
				)
			}

			Toast({ type: 'success', message: 'Usuario actualizado correctamente' })
		} catch (error) {
			Toast({ type: 'error', message: 'Error actualizando el usuario' })
		}
	}

	const handleCancel = () => {
		reset({
			name: customer?.name || '',
			lastname: customer?.lastname || '',
		})
	}

	return (
		<div className='w-full mx-auto px-3 '>
			<div className='bg-white p-4 '>
				<form onSubmit={handleSubmit(onSubmit)}>
					<div className='space-y-4 sm:space-y-6'>
						<div className='grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6'>
							<InputGroupContent
								label='Nombre'
								placeholder='Ingresa tu nombre'
								name='name'
								register={register}
								errors={errors}
								required={true}
							/>
							<InputGroupContent
								label='Apellido'
								placeholder='Ingresa tu apellido'
								name='lastname'
								register={register}
								errors={errors}
								required={true}
							/>
						</div>
					</div>

					<div className='mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200'>
						<div className='flex flex-col sm:flex-row gap-3 sm:gap-4 sm:justify-end'>
							<button
								type='button'
								onClick={handleCancel}
								className='px-6 sm:px-8 cursor-pointer py-2.5 sm:py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-300 order-2 sm:order-1 text-sm sm:text-base'
							>
								Cancelar
							</button>
							<button
								type='submit'
								disabled={isSubmitting}
								className='px-6 sm:px-8 py-2.5 sm:py-3 cursor-pointer bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-all duration-300 hover:scale-105 shadow-md hover:shadow-lg order-1 sm:order-2 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed'
							>
								{isSubmitting ? 'Guardando...' : 'Guardar cambios'}
							</button>
						</div>
					</div>
				</form>
			</div>
		</div>
	)
}

export const AccountSales = ({
	customer,
	token,
}: {
	customer: CustomersEntity | null
	token: string | null
}) => {
	const [orders, setOrders] = useState<OrdersEntity[]>([])
	const [loading, setLoading] = useState(true)
	const [expandedOrder, setExpandedOrder] = useState<string | null>(null)
	const [ratingProduct, setRatingProduct] = useState<{
		orderId: string
		productId: string
	} | null>(null)
	const [submittingRating, setSubmittingRating] = useState(false)
	const [productRatingStatus, setProductRatingStatus] = useState<
		Record<string, boolean>
	>({})
	const [verifyingProducts, setVerifyingProducts] = useState<Set<string>>(
		new Set()
	)

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
		watch,
		setValue,
	} = useForm<RatingFormData>({
		resolver: yupResolver(ratingSchema),
		defaultValues: {
			rate: 0,
			comment: '',
		},
	})

	const watchedRating = watch('rate')

	useEffect(() => {
		if (customer && token) {
			loadOrders()
		}
	}, [customer, token])

	useEffect(() => {
		if (orders.length > 0 && customer && token) {
			verifyAllProductRatings()
		}
	}, [orders, customer, token])

	const loadOrders = async () => {
		try {
			setLoading(true)
			const response = await fetchOrdersByCustomer(customer?._id ?? '', token!)
			if (response?.status === 200) {
				setOrders(response.data)
			}
		} catch (error) {
			Toast({ type: 'error', message: 'No se pudieron obtener las compras' })
		} finally {
			setLoading(false)
		}
	}

	const verifyAllProductRatings = async () => {
		const deliveredOrders = orders.filter(
			(order) => order.status === OrderStatus.delivered
		)
		const productIds: string[] = []

		deliveredOrders.forEach((order) => {
			order.products.forEach((product) => {
				if (product._id) {
					productIds.push(product._id)
				}
			})
		})

		const verificationPromises = productIds.map(async (productId) => {
			try {
				const response = await verifyProductRaiting(
					customer?._id ?? '',
					productId,
					token ?? ''
				)
				return { productId, isRated: !!response?.data?.data }
			} catch (error) {
				return { productId, isRated: false }
			}
		})

		try {
			const results = await Promise.all(verificationPromises)
			const statusMap: Record<string, boolean> = {}
			results.forEach(({ productId, isRated }) => {
				statusMap[productId] = isRated
			})
			setProductRatingStatus(statusMap)
		} catch (error) {
			console.error('Error verifying product ratings:', error)
		}
	}

	const verifyProductRating = async (productId: string) => {
		if (!customer || !token) return false

		setVerifyingProducts((prev) => new Set(prev).add(productId))

		try {
			const validProductRaithingByCustomer = await verifyProductRaiting(
				customer?._id ?? '',
				productId,
				token
			)

			if (
				[400, 401, 404, 409].includes(
					validProductRaithingByCustomer?.status ?? 0
				)
			) {
				Toast({ type: 'error', message: 'Error verificando la calificación' })
				return false
			}

			const isAlreadyRated = !!validProductRaithingByCustomer?.data?.data

			if (isAlreadyRated) {
				Toast({ type: 'info', message: 'Ya has calificado este producto' })
				setProductRatingStatus((prev) => ({ ...prev, [productId]: true }))
			}

			return !isAlreadyRated
		} catch (error) {
			Toast({ type: 'error', message: 'Error verificando la calificación' })
			return false
		} finally {
			setVerifyingProducts((prev) => {
				const newSet = new Set(prev)
				newSet.delete(productId)
				return newSet
			})
		}
	}

	const handleProductRating = async (orderId: string, productId: string) => {
		const canRate = await verifyProductRating(productId)
		if (canRate) {
			setRatingProduct({ orderId, productId })
		}
	}

	const getStatusConfig = (status: OrderStatus) => {
		const configs = {
			[OrderStatus.new]: {
				text: 'Nuevo',
				color: 'bg-gradient-to-r from-blue-500 to-blue-600',
				bgColor: 'bg-blue-50',
				textColor: 'text-blue-700',
				borderColor: 'border-blue-200',
				icon: Package,
				description: 'Tu pedido ha sido recibido y está siendo procesado',
				priority: 'low',
			},
			[OrderStatus.pending]: {
				text: 'Pendiente',
				color: 'bg-gradient-to-r from-yellow-500 to-orange-500',
				bgColor: 'bg-yellow-50',
				textColor: 'text-yellow-700',
				borderColor: 'border-yellow-200',
				icon: Clock,
				description: 'Estamos preparando tu pedido para el envío',
				priority: 'medium',
			},
			[OrderStatus.processing]: {
				text: 'Procesando',
				color: 'bg-gradient-to-r from-orange-500 to-red-500',
				bgColor: 'bg-orange-50',
				textColor: 'text-orange-700',
				borderColor: 'border-orange-200',
				icon: RefreshCw,
				description: 'Tu pedido está siendo empaquetado',
				priority: 'medium',
			},
			[OrderStatus.on_the_way]: {
				text: 'En camino',
				color: 'bg-gradient-to-r from-purple-500 to-indigo-600',
				bgColor: 'bg-purple-50',
				textColor: 'text-purple-700',
				borderColor: 'border-purple-200',
				icon: Truck,
				description: 'Tu pedido está en tránsito hacia tu dirección',
				priority: 'high',
			},
			[OrderStatus.delivered]: {
				text: 'Entregado',
				color: 'bg-gradient-to-r from-green-500 to-emerald-600',
				bgColor: 'bg-green-50',
				textColor: 'text-green-700',
				borderColor: 'border-green-200',
				icon: CheckCircle,
				description: '¡Pedido entregado exitosamente!',
				priority: 'high',
			},
			[OrderStatus.rejected]: {
				text: 'Rechazado',
				color: 'bg-gradient-to-r from-red-500 to-red-600',
				bgColor: 'bg-red-50',
				textColor: 'text-red-700',
				borderColor: 'border-red-200',
				icon: XCircle,
				description: 'Tu pedido ha sido rechazado',
				priority: 'high',
			},
			[OrderStatus.refunded]: {
				text: 'Reembolsado',
				color: 'bg-gradient-to-r from-gray-500 to-gray-600',
				bgColor: 'bg-gray-50',
				textColor: 'text-gray-700',
				borderColor: 'border-gray-200',
				icon: RefreshCw,
				description: 'El reembolso ha sido procesado',
				priority: 'medium',
			},
		}
		return configs[status] || configs[OrderStatus.new]
	}

	const calculateEstimatedDelivery = (order: OrdersEntity) => {
		if (!order.shipping_info?.estimated_date || !order._created) return null

		const createdDate = new Date(order._created)
		const estimatedDate = new Date(createdDate)
		estimatedDate.setDate(
			estimatedDate.getDate() + order.shipping_info.estimated_date
		)

		const today = new Date()
		const diffTime = estimatedDate.getTime() - today.getTime()
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

		return { days: diffDays, date: estimatedDate }
	}

	const handleRatingSubmit = async (data: RatingFormData) => {
		if (!customer || !token || !ratingProduct) return

		try {
			setSubmittingRating(true)

			const ratingData = {
				customer: customer._id,
				rate: data.rate,
				comment: data.comment,
			}

			const response = await createProductRaiting(
				ratingProduct.productId,
				ratingData,
				token
			)

			if (response && (response.status === 200 || response.status === 201)) {
				setProductRatingStatus((prev) => ({
					...prev,
					[ratingProduct.productId]: true,
				}))
				setRatingProduct(null)
				reset()
				Toast({ type: 'success', message: '¡Gracias por tu calificación!' })
			}
		} catch (error) {
			Toast({ type: 'error', message: 'No se pudo agregar la calificación' })
		} finally {
			setSubmittingRating(false)
		}
	}

	const StarRating = ({
		rating,
		onRatingChange,
		readOnly = false,
	}: {
		rating: number
		onRatingChange?: (rating: number) => void
		readOnly?: boolean
	}) => {
		return (
			<div className='flex gap-1'>
				{[1, 2, 3, 4, 5].map((star) => (
					<button
						key={star}
						type='button'
						disabled={readOnly}
						onClick={() => !readOnly && onRatingChange?.(star)}
						className={`transition-all duration-200 ${
							!readOnly ? 'hover:scale-110 hover:drop-shadow-sm' : ''
						}`}
					>
						<StarIcon
							className={`w-5 h-5 sm:w-6 sm:h-6 transition-colors ${
								star <= rating
									? 'text-amber-400 fill-current drop-shadow-sm'
									: 'text-gray-300 hover:text-amber-200'
							}`}
						/>
					</button>
				))}
			</div>
		)
	}

	const getOrderAlert = (order: OrdersEntity) => {
		if (
			order.status === OrderStatus.on_the_way &&
			hasShippingInfo(order.shipping_info)
		) {
			return {
				type: 'info',
				message: 'Tu pedido está en camino',
				details: order.shipping_info.company
					? `Empresa: ${order.shipping_info.company}`
					: 'En tránsito',
			}
		}

		if (order.status === OrderStatus.rejected && order.denial?.reason) {
			return {
				type: 'error',
				message: 'Pedido rechazado',
				details: order.denial.reason,
			}
		}

		if (order.status === OrderStatus.refunded && order.refunded?.reason) {
			return {
				type: 'warning',
				message: 'Pedido reembolsado',
				details: order.refunded.reason,
			}
		}

		if (order.status === OrderStatus.delivered) {
			return {
				type: 'success',
				message: '¡Pedido entregado!',
				details: 'Gracias por tu compra',
			}
		}

		return null
	}

	const hasShippingInfo = (shippingInfo: ShippingInfo | null) => {
		if (!shippingInfo) return false
		return !!(
			shippingInfo.company ||
			shippingInfo.code ||
			shippingInfo.company_address ||
			shippingInfo.date
		)
	}

	const hasCouponDiscount = (order: OrdersEntity) => {
		return (
			order.coupon &&
			order.coupon.code &&
			((order.coupon.type === TypesCoupon.AMOUNT &&
				order.coupon.amount &&
				order.coupon.amount > 0) ||
				(order.coupon.type === TypesCoupon.DISCOUNT &&
					order.coupon.discount &&
					order.coupon.discount > 0))
		)
	}

	const getTotalSavings = (order: OrdersEntity) => {
		let totalSavings = 0

		order.products.forEach((product) => {
			totalSavings += product.productDiscount || 0
			totalSavings += product.couponDiscount || 0
		})

		return totalSavings
	}

	const getOrderSummary = (order: OrdersEntity) => {
		const subtotal = order.products.reduce(
			(sum, product) => sum + product.subtotal,
			0
		)
		const totalDiscount = getTotalSavings(order)
		const finalTotal = order.products.reduce(
			(sum, product) => sum + product.total,
			0
		)

		return {
			subtotal,
			totalDiscount,
			finalTotal,
		}
	}

	const getUnratedProductsCount = () => {
		let count = 0
		orders.forEach((order) => {
			if (order.status === OrderStatus.delivered) {
				order.products.forEach((product) => {
					if (product._id && !productRatingStatus[product._id]) {
						count++
					}
				})
			}
		})
		return count
	}

	const pendingRatingsCount = getUnratedProductsCount()

	const getAlertStyles = (type: string) => {
		switch (type) {
			case 'success':
				return 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 text-green-800'
			case 'error':
				return 'bg-gradient-to-r from-red-50 to-pink-50 border-red-200 text-red-800'
			case 'warning':
				return 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200 text-amber-800'
			case 'info':
				return 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 text-blue-800'
			default:
				return 'bg-gray-50 border-gray-200 text-gray-800'
		}
	}

	if (loading) {
		return (
			<div className='w-full space-y-3 sm:space-y-4'>
				<div className='animate-pulse'>
					<div className='h-6 sm:h-8 bg-gray-200 rounded-lg w-1/3 mb-4 sm:mb-6'></div>
				</div>
				{[1, 2, 3].map((i) => (
					<div
						key={i}
						className='bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 animate-pulse border border-gray-100 shadow-sm'
					>
						<div className='flex items-center space-x-3 sm:space-x-4'>
							<div className='w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-lg sm:rounded-xl'></div>
							<div className='flex-1 space-y-2 sm:space-y-3'>
								<div className='h-3 sm:h-4 bg-gray-200 rounded w-3/4'></div>
								<div className='h-2 sm:h-3 bg-gray-200 rounded w-1/2'></div>
								<div className='h-2 sm:h-3 bg-gray-200 rounded w-1/4'></div>
							</div>
							<div className='w-12 sm:w-16 h-3 sm:h-4 bg-gray-200 rounded'></div>
						</div>
					</div>
				))}
			</div>
		)
	}

	return (
		<div className='w-full space-y-4 sm:space-y-6 px-2 sm:px-0'>
			<div className='flex items-center justify-between'>
				<h2 className='text-xl sm:text-2xl font-bold text-gray-900'>
					Mis Compras
				</h2>
				{orders.length > 0 && (
					<span className='text-xs sm:text-sm text-gray-500 bg-gray-100 px-2 py-1 sm:px-3 sm:py-1 rounded-full'>
						{orders.length} pedido{orders.length > 1 ? 's' : ''}
					</span>
				)}
			</div>

			{pendingRatingsCount > 0 && (
				<div className='bg-gradient-to-r mt-4 from-amber-50 via-orange-50 to-red-50 rounded-lg sm:rounded-xl border border-amber-200 p-3 sm:p-4 shadow-sm'>
					<div className='flex items-center justify-between flex-col sm:flex-row space-y-3 sm:space-y-0'>
						<div className='flex items-center space-x-3 sm:space-x-4'>
							<div className='w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg sm:rounded-xl flex items-center justify-center shadow-md'>
								<Star className='w-5 h-5 sm:w-6 sm:h-6 text-white' />
							</div>
							<div>
								<h3 className='text-base sm:text-lg font-semibold text-gray-900'>
									{pendingRatingsCount} producto
									{pendingRatingsCount > 1 ? 's' : ''} por calificar
								</h3>
								<p className='text-xs sm:text-sm text-gray-600'>
									Comparte tu experiencia con otros compradores
								</p>
							</div>
						</div>
						{/* <button
							onClick={() => {
								const firstUnrated = getFirstUnratedProduct()
								if (firstUnrated) {
									handleProductRating(
										firstUnrated.orderId,
										firstUnrated.productId
									)
								}
							}}
							className='px-4 py-2 sm:px-6 sm:py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-lg sm:rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all transform hover:scale-105 shadow-md hover:shadow-lg text-sm sm:text-base'
						>
							Calificar ahora
						</button> */}
					</div>
				</div>
			)}

			<div className='space-y-3 sm:space-y-4'>
				{orders.map((order) => {
					const statusConfig = getStatusConfig(order.status)
					const StatusIcon = statusConfig.icon
					const estimatedDelivery = calculateEstimatedDelivery(order)
					const isExpanded = expandedOrder === order._id
					const alert = getOrderAlert(order)
					const orderSummary = getOrderSummary(order)

					return (
						<div
							key={order._id}
							className='bg-white rounded-lg sm:rounded-xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300'
						>
							{alert && (
								<div
									className={`px-4 py-2 sm:px-6 sm:py-3 border-b ${getAlertStyles(
										alert.type
									)}`}
								>
									<div className='flex items-center space-x-2 sm:space-x-3'>
										<div className='flex-shrink-0'>
											{alert.type === 'success' && (
												<CheckCircle className='w-4 h-4 sm:w-5 sm:h-5' />
											)}
											{alert.type === 'error' && (
												<XCircle className='w-4 h-4 sm:w-5 sm:h-5' />
											)}
											{alert.type === 'warning' && (
												<AlertTriangle className='w-4 h-4 sm:w-5 sm:h-5' />
											)}
											{alert.type === 'info' && (
												<Info className='w-4 h-4 sm:w-5 sm:h-5' />
											)}
										</div>
										<div className='flex-1 min-w-0'>
											<p className='font-semibold text-xs sm:text-sm truncate'>
												{alert.message}
											</p>
											{alert.details && (
												<p className='text-xs mt-1 opacity-90 truncate'>
													{alert.details}
												</p>
											)}
										</div>
									</div>
								</div>
							)}

							<div className='p-4 sm:p-6'>
								<div className='flex items-start justify-between mb-3 sm:mb-4'>
									<div className='flex items-start space-x-3 sm:space-x-4 flex-1 min-w-0'>
										<div
											className={`w-10 h-10 sm:w-12 sm:h-12 ${statusConfig.color} rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 shadow-md`}
										>
											<StatusIcon className='w-5 h-5 sm:w-6 sm:h-6 text-white' />
										</div>
										<div className='flex-1 min-w-0'>
											<div className='flex items-center space-x-2 sm:space-x-3 mb-2 flex-wrap'>
												<h3 className='text-base sm:text-lg font-bold text-gray-900 truncate'>
													#{order.code}
												</h3>
												<span
													className={`px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs font-semibold border ${statusConfig.bgColor} ${statusConfig.textColor} ${statusConfig.borderColor} whitespace-nowrap`}
												>
													{statusConfig.text}
												</span>
												{hasCouponDiscount(order) && (
													<span className='px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200 whitespace-nowrap'>
														Con cupón
													</span>
												)}
											</div>
											<p className='text-xs sm:text-sm text-gray-600 mb-2 line-clamp-2'>
												{statusConfig.description}
											</p>
											{estimatedDelivery &&
												order.status !== OrderStatus.delivered && (
													<div className='flex items-center space-x-2 mb-2'>
														<Clock className='w-3 h-3 sm:w-4 sm:h-4 text-gray-400' />
														<p className='text-xs sm:text-sm text-gray-500'>
															{estimatedDelivery.days > 0
																? `${estimatedDelivery.days} días restantes`
																: estimatedDelivery.days === 0
																? 'Llega hoy'
																: 'Fecha estimada pendiente'}
														</p>
													</div>
												)}
											{order._created && (
												<div className='flex items-center space-x-2'>
													<Calendar className='w-3 h-3 sm:w-4 sm:h-4 text-gray-400' />
													<p className='text-xs sm:text-sm text-gray-500'>
														{new Date(order._created).toLocaleDateString(
															'es-ES',
															{
																day: 'numeric',
																month: 'short',
																year: 'numeric',
															}
														)}
													</p>
												</div>
											)}
										</div>
									</div>
									<div className='flex items-center space-x-3 sm:space-x-4 flex-shrink-0'>
										<div className='text-right'>
											{orderSummary.totalDiscount > 0 && (
												<p className='text-xs text-green-600 font-medium mb-1'>
													¡Ahorraste $
													{orderSummary.totalDiscount.toLocaleString()}!
												</p>
											)}
											<p className='text-xs sm:text-sm text-gray-500'>Total</p>
											<span className='text-lg sm:text-xl font-bold text-gray-900'>
												${orderSummary.finalTotal.toLocaleString()}
											</span>
										</div>
										<button
											onClick={() =>
												setExpandedOrder(isExpanded ? null : order._id)
											}
											className={`p-1.5 cursor-pointer sm:p-2 rounded-lg sm:rounded-xl transition-all ${
												isExpanded
													? 'bg-red-50 text-red-600'
													: 'hover:bg-gray-100 text-gray-500'
											}`}
										>
											{isExpanded ? (
												<ChevronUp className='w-4 h-4 sm:w-5 sm:h-5' />
											) : (
												<ChevronDown className='w-4 h-4 sm:w-5 sm:h-5' />
											)}
										</button>
									</div>
								</div>

								{order.products.length > 0 && (
									<div className='flex items-center space-x-3 mb-3 sm:mb-4'>
										<div className='flex -space-x-2'>
											{order.products
												.slice(0, 4)
												.flatMap((product) => product.attributes || [])
												.slice(0, 8)
												.map((attr, idx) => (
													<div
														key={`${attr._id}-${idx}`}
														className='w-8 h-8 sm:w-10 sm:h-10 rounded-md sm:rounded-lg border-2 border-white bg-gray-100 overflow-hidden shadow-sm'
													>
														{attr?.image?.url ? (
															<img
																src={`${API_URL}/${attr.image.url}`}
																alt={`attribute-image-${attr._id}`}
																className='w-full h-full object-cover hover:scale-110 transition-transform duration-200'
															/>
														) : (
															<div className='w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200'>
																<Package className='w-3 h-3 sm:w-4 sm:h-4 text-gray-400' />
															</div>
														)}
													</div>
												))}

											{order.products.length > 4 && (
												<div className='w-8 h-8 sm:w-10 sm:h-10 rounded-md sm:rounded-lg border-2 border-white bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center shadow-sm'>
													<span className='text-xs font-bold text-gray-600'>
														+{order.products.length - 4}
													</span>
												</div>
											)}
										</div>

										<div>
											<p className='text-xs sm:text-sm font-medium text-gray-900'>
												{order.products.length} producto
												{order.products.length > 1 ? 's' : ''}
											</p>
											<p className='text-xs text-gray-500'>
												{order.products.reduce(
													(total, product) => total + product.totalQuantity,
													0
												)}{' '}
												unidades
											</p>
										</div>
									</div>
								)}

								<div className='flex gap-2 sm:gap-3'>
									<button
										onClick={() =>
											setExpandedOrder(isExpanded ? null : order._id)
										}
										className='flex-1 py-2 cursor-pointer sm:py-3 px-3 sm:px-4 text-xs sm:text-sm font-semibold text-gray-700 border-2 border-gray-200 rounded-lg sm:rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all'
									>
										{isExpanded ? 'Ocultar detalles' : 'Ver detalles'}
									</button>
								</div>
							</div>

							{isExpanded && (
								<div className='border-t border-gray-100 bg-gradient-to-b from-gray-50 to-white p-4 sm:p-6 space-y-4 sm:space-y-6'>
									{hasCouponDiscount(order) && (
										<div className='bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-green-200'>
											<h4 className='text-base sm:text-lg font-semibold text-gray-900 flex items-center mb-3'>
												<Tag className='w-4 h-4 sm:w-5 sm:h-5 mr-2 text-green-600' />
												Cupón aplicado
											</h4>
											<div className='grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4'>
												<div className='flex items-center space-x-3 p-2 sm:p-3 bg-white rounded-lg shadow-sm'>
													<div className='w-6 h-6 sm:w-8 sm:h-8 bg-green-500 rounded-lg flex items-center justify-center'>
														<Percent className='w-3 h-3 sm:w-4 sm:h-4 text-white' />
													</div>
													<div>
														<p className='text-xs text-green-600 font-medium'>
															Código
														</p>
														<p className='text-xs sm:text-sm text-green-800 font-bold'>
															{order.coupon?.code}
														</p>
													</div>
												</div>
												<div className='flex items-center space-x-3 p-2 sm:p-3 bg-white rounded-lg shadow-sm'>
													<div className='w-6 h-6 sm:w-8 sm:h-8 bg-emerald-500 rounded-lg flex items-center justify-center'>
														<DollarSign className='w-3 h-3 sm:w-4 sm:h-4 text-white' />
													</div>
													<div>
														<p className='text-xs text-emerald-600 font-medium'>
															Descuento
														</p>
														<p className='text-xs sm:text-sm text-emerald-800 font-bold'>
															{order.coupon?.type === TypesCoupon.AMOUNT
																? `${order.coupon.amount?.toLocaleString()}`
																: `${order.coupon.discount}%`}
														</p>
													</div>
												</div>
											</div>
										</div>
									)}

									<div className='bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 border border-gray-100 shadow-sm'>
										<h4 className='text-base sm:text-lg font-semibold text-gray-900 flex items-center mb-3 sm:mb-4'>
											<Receipt className='w-4 h-4 sm:w-5 sm:h-5 mr-2 text-indigo-600' />
											Resumen de compra
										</h4>
										<div className='space-y-2 sm:space-y-3'>
											<div className='flex justify-between items-center py-1 sm:py-2'>
												<span className='text-sm sm:text-base text-gray-600'>
													Subtotal
												</span>
												<span className='font-semibold text-sm sm:text-base'>
													${orderSummary.subtotal.toLocaleString()}
												</span>
											</div>

											<div className='flex justify-between items-center py-1 sm:py-2'>
												<span className='text-sm sm:text-base text-gray-600'>
													Envío
												</span>
												<span className='font-semibold text-sm sm:text-base'>
													{formatCurrency(
														order.products.reduce(
															(total, product) =>
																total + (product?.totalShipping || 0),
															0
														)
													)}
												</span>
											</div>

											{orderSummary.totalDiscount > 0 && (
												<div className='flex justify-between items-center py-1 sm:py-2 text-green-600'>
													<span className='text-sm sm:text-base'>
														Descuentos
													</span>
													<span className='font-semibold text-sm sm:text-base'>
														-${orderSummary.totalDiscount.toLocaleString()}
													</span>
												</div>
											)}
											<hr className='border-gray-200' />
											<div className='flex justify-between items-center py-1 sm:py-2 text-base sm:text-lg font-bold'>
												<span>Total</span>
												<span>${orderSummary.finalTotal.toLocaleString()}</span>
											</div>
										</div>
									</div>

									<div className='space-y-3 sm:space-y-4'>
										<h4 className='text-base sm:text-lg font-semibold text-gray-900 flex items-center'>
											<ShoppingBag className='w-4 h-4 sm:w-5 sm:h-5 mr-2 text-red-600' />
											Productos
										</h4>
										{order.products.map((product, idx) => (
											<div
												key={product._id || idx}
												className='p-3 sm:p-4 bg-white rounded-lg sm:rounded-xl border border-gray-100 shadow-sm'
											>
												<div className='flex items-start space-x-3 sm:space-x-4 mb-4'>
													<div className='w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-lg sm:rounded-xl flex-shrink-0 overflow-hidden shadow-sm'>
														{product.attributes?.[0]?.image?.url ? (
															<img
																src={`${API_URL}/${product.attributes[0].image.url}`}
																alt={product.name}
																className='w-full h-full object-cover'
															/>
														) : (
															<div className='w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200'>
																<Package className='w-4 h-4 sm:w-6 sm:h-6 text-gray-400' />
															</div>
														)}
													</div>
													<div className='flex-1 min-w-0'>
														<div className='flex items-start justify-between mb-2'>
															<div>
																<h5 className='text-sm sm:text-base font-semibold text-gray-900 mb-1'>
																	{product.name}
																</h5>
																{product.description && (
																	<p className='text-xs sm:text-sm text-gray-600 mb-2 line-clamp-2'>
																		{product.description}
																	</p>
																)}
																{product.attributes &&
																	product.attributes?.[0]?.value?.length >
																		0 && (
																		<div className='flex flex-wrap gap-1 sm:gap-2 mb-2 sm:mb-3'>
																			{product.attributes[0].value.map(
																				(value, valueIdx) => (
																					<span
																						key={valueIdx}
																						className='px-1.5 py-0.5 sm:px-2 sm:py-1 bg-blue-100 text-blue-800 text-xs rounded-md font-medium'
																					>
																						{value}
																					</span>
																				)
																			)}
																		</div>
																	)}
																<div className='flex items-center space-x-2 text-xs sm:text-sm'>
																	<span className='flex items-center text-gray-600'>
																		<Package className='w-3 h-3 sm:w-4 sm:h-4 mr-1' />
																		{product.totalQuantity} und.
																	</span>
																	{product.brand && (
																		<span className='px-1.5 py-0.5 sm:px-2 sm:py-1 bg-gray-100 rounded-md font-medium text-gray-700'>
																			{product.brand}
																		</span>
																	)}
																	{product.discount > 0 && (
																		<span className='px-1.5 py-0.5 sm:px-2 sm:py-1 bg-red-100 text-red-700 rounded-md font-medium'>
																			{product.discount}% OFF
																		</span>
																	)}
																</div>
															</div>
															<div className='text-right'>
																{(product.productDiscount > 0 ||
																	product.couponDiscount > 0) && (
																	<p className='text-xs text-gray-500 line-through mb-1'>
																		${product.subtotal.toLocaleString()}
																	</p>
																)}
																<span className='text-base sm:text-lg font-bold text-gray-900'>
																	${product.total.toLocaleString()}
																</span>
																{product.totalShipping > 0 && (
																	<p className='text-xs text-emerald-600 mt-1 flex items-center justify-end'>
																		<Truck className='w-3 h-3 mr-1' />$
																		{product.totalShipping.toLocaleString()}{' '}
																		envío
																	</p>
																)}
															</div>
														</div>
													</div>
												</div>

												{product.attributes &&
													product.attributes.length > 0 && (
														<div className='border-t border-gray-100 pt-4'>
															<h6 className='text-sm font-semibold text-gray-700 mb-3 flex items-center'>
																<Tag className='w-4 h-4 mr-2 text-blue-600' />
																Detalles por variante
															</h6>
															<div className='space-y-3'>
																{product.attributes.map(
																	(attribute, attrIdx) => (
																		<div
																			key={attribute._id || attrIdx}
																			className='flex items-center space-x-3 p-3 bg-gray-50 rounded-lg border border-gray-100'
																		>
																			<div className='w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-md flex-shrink-0 overflow-hidden shadow-sm border'>
																				{attribute.image?.url ? (
																					<img
																						src={`${API_URL}/${attribute.image.url}`}
																						alt={
																							attribute.value?.join(', ') ||
																							'Atributo'
																						}
																						className='w-full h-full object-cover'
																					/>
																				) : (
																					<div className='w-full h-full flex items-center justify-center bg-gray-100'>
																						<Package className='w-3 h-3 text-gray-400' />
																					</div>
																				)}
																			</div>

																			<div className='flex-1 min-w-0'>
																				{attribute.value &&
																					attribute.value.length > 0 && (
																						<div className='flex flex-wrap gap-1 mb-2'>
																							{attribute.value.map(
																								(value, valueIdx) => (
																									<span
																										key={valueIdx}
																										className='px-1.5 py-0.5 sm:px-2 sm:py-1 bg-blue-100 text-blue-800 text-xs rounded-md font-medium'
																									>
																										{value}
																									</span>
																								)
																							)}
																						</div>
																					)}
																				<div className='flex items-center space-x-3 text-xs text-gray-600'>
																					<span className='flex items-center'>
																						<Package className='w-3 h-3 mr-1' />
																						{attribute.quantity} und.
																					</span>
																					<span>
																						$
																						{attribute.price?.toLocaleString() ||
																							0}{' '}
																						c/u
																					</span>
																				</div>
																			</div>

																			<div className='text-right flex-shrink-0'>
																				{(attribute.productDiscount > 0 ||
																					attribute.couponDiscount > 0) && (
																					<p className='text-xs text-gray-500 line-through mb-1'>
																						$
																						{attribute.subtotal?.toLocaleString() ||
																							0}
																					</p>
																				)}
																				<div className='font-semibold text-gray-900'>
																					$
																					{attribute.total?.toLocaleString() ||
																						0}
																				</div>
																				{product.shipping_info
																					?.isHaveShipping &&
																					product.shipping_info?.price > 0 && (
																						<div className='text-xs text-emerald-600 mt-1'>
																							<div className='flex items-center justify-end'>
																								<Truck className='w-3 h-3 mr-1' />
																								$
																								{(() => {
																									const baseShipping =
																										product.shipping_info.price
																									const qty = attribute.quantity
																									if (qty === 1) {
																										return baseShipping.toLocaleString()
																									} else {
																										const additionalCost =
																											baseShipping *
																											0.5 *
																											(qty - 1)
																										return (
																											baseShipping +
																											additionalCost
																										).toLocaleString()
																									}
																								})()}
																							</div>
																							{attribute.quantity > 1 && (
																								<div className='text-xs text-gray-500 mt-1'>
																									Base: $
																									{product.shipping_info.price.toLocaleString()}{' '}
																									+ {attribute.quantity - 1} ×
																									50%
																								</div>
																							)}
																						</div>
																					)}
																			</div>

																			{order.status === OrderStatus.delivered &&
																				product._id && (
																					<div className='ml-3 flex-shrink-0'>
																						{productRatingStatus[
																							`${product._id}-${attribute._id}`
																						] ? (
																							<div className='flex items-center space-x-2 px-2 py-1 bg-green-50 rounded-md border border-green-200'>
																								<CheckCircle className='w-3 h-3 text-green-600' />
																								<span className='text-xs font-medium text-green-700'>
																									Ya calificado
																								</span>
																							</div>
																						) : (
																							<button
																								onClick={() =>
																									handleProductRating(
																										order._id,
																										product._id!
																									)
																								}
																								disabled={verifyingProducts.has(
																									`${product._id}-${attribute._id}`
																								)}
																								className='flex cursor-pointer items-center space-x-2 px-2 py-1 text-xs font-semibold bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-md hover:from-blue-600 hover:to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 shadow-sm hover:shadow-md'
																							>
																								{verifyingProducts.has(
																									`${product._id}-${attribute._id}`
																								) ? (
																									<>
																										<div className='animate-spin rounded-full h-2 w-2 border-b-2 border-white'></div>
																										<span>Verificando...</span>
																									</>
																								) : (
																									<>
																										<Star className='w-3 h-3' />
																										<span>Calificar</span>
																									</>
																								)}
																							</button>
																						)}
																					</div>
																				)}
																		</div>
																	)
																)}
															</div>
														</div>
													)}

												{order.status === OrderStatus.delivered &&
													product._id && (
														<div className='flex justify-end mt-4 pt-4 border-t border-gray-100'>
															{productRatingStatus[product._id] ? (
																<div className='flex items-center space-x-2 px-3 py-2 bg-green-50 rounded-lg border border-green-200'>
																	<CheckCircle className='w-4 h-4 text-green-600' />
																	<span className='text-xs sm:text-sm font-medium text-green-700'>
																		Ya calificado
																	</span>
																</div>
															) : (
																<button
																	onClick={() =>
																		handleProductRating(order._id, product._id!)
																	}
																	disabled={verifyingProducts.has(product._id)}
																	className='flex cursor-pointer items-center space-x-2 px-3 py-2 sm:px-4 sm:py-2 text-xs sm:text-sm font-semibold bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 shadow-md hover:shadow-lg'
																>
																	{verifyingProducts.has(product._id) ? (
																		<>
																			<div className='animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1'></div>
																			<span>Verificando...</span>
																		</>
																	) : (
																		<>
																			<Star className='w-3 h-3 sm:w-4 sm:h-4' />
																			<span>Calificar</span>
																		</>
																	)}
																</button>
															)}
														</div>
													)}
											</div>
										))}
									</div>

									{order?.products[0].type !== Entities.PLANS_PERSONALIZED &&
										hasShippingInfo(order.shipping_info) && (
											<div className='bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 border border-gray-100 shadow-sm'>
												<h5 className='text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center'>
													<Truck className='w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-600' />
													Información de Envío
												</h5>
												<div className='grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4'>
													{order.shipping_info?.company && (
														<div className='flex items-center space-x-2 sm:space-x-3 p-2 sm:p-3 bg-blue-50 rounded-lg'>
															<div className='w-6 h-6 sm:w-8 sm:h-8 bg-blue-500 rounded-lg flex items-center justify-center'>
																<Truck className='w-3 h-3 sm:w-4 sm:h-4 text-white' />
															</div>
															<div>
																<p className='text-xs text-blue-600 font-medium'>
																	Empresa
																</p>
																<p className='text-xs sm:text-sm text-blue-800 font-semibold'>
																	{order.shipping_info.company}
																</p>
															</div>
														</div>
													)}
													{order.shipping_info?.code && (
														<div className='flex items-center space-x-2 sm:space-x-3 p-2 sm:p-3 bg-purple-50 rounded-lg'>
															<div className='w-6 h-6 sm:w-8 sm:h-8 bg-purple-500 rounded-lg flex items-center justify-center'>
																<Package className='w-3 h-3 sm:w-4 sm:h-4 text-white' />
															</div>
															<div>
																<p className='text-xs text-purple-600 font-medium'>
																	Código de seguimiento
																</p>
																<p className='text-xs sm:text-sm text-purple-800 font-semibold'>
																	{order.shipping_info.code}
																</p>
															</div>
														</div>
													)}
													{order.shipping_info?.company_address && (
														<div className='flex items-center space-x-2 sm:space-x-3 p-2 sm:p-3 bg-green-50 rounded-lg sm:col-span-2'>
															<div className='w-6 h-6 sm:w-8 sm:h-8 bg-green-500 rounded-lg flex items-center justify-center'>
																<MapPin className='w-3 h-3 sm:w-4 sm:h-4 text-white' />
															</div>
															<div>
																<p className='text-xs text-green-600 font-medium'>
																	Dirección
																</p>
																<p className='text-xs sm:text-sm text-green-800 font-semibold'>
																	{order.shipping_info.company_address}
																</p>
															</div>
														</div>
													)}
													{order.shipping_info?.date && (
														<div className='flex items-center space-x-2 sm:space-x-3 p-2 sm:p-3 bg-orange-50 rounded-lg'>
															<div className='w-6 h-6 sm:w-8 sm:h-8 bg-orange-500 rounded-lg flex items-center justify-center'>
																<Calendar className='w-3 h-3 sm:w-4 sm:h-4 text-white' />
															</div>
															<div>
																<p className='text-xs text-orange-600 font-medium'>
																	Fecha de envío
																</p>
																<p className='text-xs sm:text-sm text-orange-800 font-semibold'>
																	{new Date(
																		order.shipping_info.date
																	).toLocaleDateString('es-ES')}
																</p>
															</div>
														</div>
													)}
													{order.shipping_info?.type && (
														<div className='flex items-center space-x-2 sm:space-x-3 p-2 sm:p-3 bg-indigo-50 rounded-lg'>
															<div className='w-6 h-6 sm:w-8 sm:h-8 bg-indigo-500 rounded-lg flex items-center justify-center'>
																<CreditCard className='w-3 h-3 sm:w-4 sm:h-4 text-white' />
															</div>
															<div>
																<p className='text-xs text-indigo-600 font-medium'>
																	Tipo de pago
																</p>
																<p className='text-xs sm:text-sm text-indigo-800 font-semibold'>
																	{order.shipping_info.type ===
																	ShippingType.payed
																		? 'Pagado'
																		: 'Pago contra entrega'}
																</p>
															</div>
														</div>
													)}
													{order.shipping_info?.estimated_date &&
														order.shipping_info.estimated_date > 0 && (
															<div className='flex items-center space-x-2 sm:space-x-3 p-2 sm:p-3 bg-emerald-50 rounded-lg'>
																<div className='w-6 h-6 sm:w-8 sm:h-8 bg-emerald-500 rounded-lg flex items-center justify-center'>
																	<CalendarFold className='w-3 h-3 sm:w-4 sm:h-4 text-white' />
																</div>
																<div>
																	<p className='text-xs text-emerald-600 font-medium'>
																		Dias estimados para la entrega
																	</p>
																	<p className='text-xs sm:text-sm text-emerald-800 font-semibold'>
																		{order.shipping_info.estimated_date} días
																	</p>
																</div>
															</div>
														)}
												</div>
												{order.shipping_info?.evidenced?.url && (
													<div className='mt-3 sm:mt-4'>
														<p className='text-xs sm:text-sm font-medium text-gray-700 mb-2'>
															Comprobante de envio
														</p>
														<div className='relative inline-block'>
															<img
																src={`${API_URL}/${order.shipping_info.evidenced.url}`}
																alt='Evidencia de envío'
																className='w-full max-w-xs sm:max-w-md rounded-lg sm:rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow'
															/>

															<div className='absolute top-1 right-1 sm:top-2 sm:right-2 p-1 bg-black bg-opacity-50 rounded-lg'>
																<button
																	onClick={async () => {
																		const response = await fetch(
																			`${API_URL}/${order.shipping_info.evidenced.url}`
																		)
																		const blob = await response.blob()
																		const url = window.URL.createObjectURL(blob)
																		const a = document.createElement('a')
																		a.href = url
																		a.download =
																			`Comprobante-de-envio-${order?.code}` ||
																			'evidencia.png'
																		a.click()
																		window.URL.revokeObjectURL(url)
																	}}
																	className='flex items-center cursor-pointer'
																>
																	<Download className='w-3 h-3 sm:w-4 sm:h-4 text-white' />
																</button>
															</div>
														</div>
													</div>
												)}
											</div>
										)}

									{order.status === OrderStatus.delivered && order.bill && (
										<div className='bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 border border-gray-100 shadow-sm'>
											<h5 className='text-base sm:text-lg font-semibold text-gray-900 mb-3 flex items-center'>
												<FileText className='w-4 h-4 sm:w-5 sm:h-5 mr-2 text-indigo-600' />
												Factura
											</h5>
											<button
												onClick={() =>
													window.open(`${API_URL}/${order.bill.url}`, '_blank')
												}
												className='flex cursor-pointer items-center space-x-2 sm:space-x-3 p-2 sm:p-3 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors w-full'
											>
												<div className='w-6 h-6 sm:w-8 sm:h-8 bg-indigo-500 rounded-lg flex items-center justify-center'>
													<Download className='w-3 h-3 sm:w-4 sm:h-4 text-white' />
												</div>
												<div className='text-left'>
													<p className='text-xs sm:text-sm font-semibold text-indigo-800'>
														Descargar factura PDF
													</p>
													<p className='text-xs text-indigo-600'>
														Guarda tu comprobante
													</p>
												</div>
											</button>
										</div>
									)}
								</div>
							)}
						</div>
					)
				})}
			</div>

			{orders.length === 0 && !loading && (
				<div className='bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl p-8 sm:p-12 text-center border border-gray-200 mx-2 sm:mx-0'>
					<div className='w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-gray-200 to-gray-300 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-sm'>
						<ShoppingBag className='w-8 h-8 sm:w-10 sm:h-10 text-gray-400' />
					</div>
					<h3 className='text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3'>
						Aún no tienes pedidos
					</h3>
					<p className='text-sm sm:text-base text-gray-600 mb-4 sm:mb-6'>
						Explora nuestro catálogo y encuentra los mejores productos
						deportivos para alcanzar tus metas
					</p>
					<Link
						to='/'
						className='px-5 py-2 sm:px-6 sm:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-lg sm:rounded-xl hover:from-red-600 hover:to-red-700 transition-all transform hover:scale-105 shadow-md hover:shadow-lg text-sm sm:text-base'
					>
						Explorar productos
					</Link>
				</div>
			)}

			{ratingProduct && (
				<div className='fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-3 sm:p-4 backdrop-blur-sm'>
					<div className='bg-white rounded-xl sm:rounded-2xl max-w-sm sm:max-w-md w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto shadow-2xl'>
						<div className='p-4 sm:p-6'>
							<div className='flex items-center justify-between mb-4 sm:mb-6'>
								<div className='flex items-center space-x-2 sm:space-x-3'>
									<div className='w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg sm:rounded-xl flex items-center justify-center'>
										<Star className='w-4 h-4 sm:w-5 sm:h-5 text-white' />
									</div>
									<h3 className='text-lg sm:text-xl font-bold text-gray-900'>
										Calificar producto
									</h3>
								</div>
								<button
									onClick={() => setRatingProduct(null)}
									className='p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg sm:rounded-xl transition-colors'
								>
									<XCircle className='w-5 h-5 sm:w-6 sm:h-6 text-gray-500' />
								</button>
							</div>

							<form
								onSubmit={handleSubmit(handleRatingSubmit)}
								className='space-y-4 sm:space-y-6'
							>
								<div>
									<label className='block text-xs sm:text-sm font-semibold text-gray-700 mb-2 sm:mb-3'>
										¿Cómo calificarías tu experiencia? *
									</label>
									<div className='flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl'>
										<StarRating
											rating={watchedRating}
											onRatingChange={(rating) => setValue('rate', rating)}
										/>
										<div className='text-right'>
											<span className='text-base sm:text-lg font-bold text-gray-900'>
												{watchedRating > 0 ? `${watchedRating}/5` : '0/5'}
											</span>
											<p className='text-xs text-gray-500 mt-1'>
												{watchedRating === 0 && 'Sin calificar'}
												{watchedRating === 1 && 'Muy malo'}
												{watchedRating === 2 && 'Malo'}
												{watchedRating === 3 && 'Regular'}
												{watchedRating === 4 && 'Bueno'}
												{watchedRating === 5 && 'Excelente'}
											</p>
										</div>
									</div>
									{errors.rate && (
										<p className='mt-2 text-xs sm:text-sm text-red-600 flex items-center'>
											<AlertCircle className='w-3 h-3 sm:w-4 sm:h-4 mr-1' />
											{errors.rate.message}
										</p>
									)}
								</div>

								<div>
									<label className='block text-xs sm:text-sm font-semibold text-gray-700 mb-2 sm:mb-3'>
										Cuéntanos tu experiencia *
									</label>
									<textarea
										{...register('comment')}
										rows={4}
										className='w-full px-3 py-2 sm:px-4 sm:py-3 border-2 border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none text-xs sm:text-sm transition-all'
										placeholder='Comparte los detalles de tu experiencia con este producto...'
									/>
									{errors.comment && (
										<p className='mt-2 text-xs sm:text-sm text-red-600 flex items-center'>
											<AlertCircle className='w-3 h-3 sm:w-4 sm:h-4 mr-1' />
											{errors.comment.message}
										</p>
									)}
									<div className='flex justify-between items-center mt-2'>
										<p className='text-xs text-gray-500'>
											Mínimo 10 caracteres
										</p>
										<p className='text-xs text-gray-500'>
											{watch('comment')?.length || 0}/500
										</p>
									</div>
								</div>

								<div className='flex space-x-2 sm:space-x-3 pt-3 sm:pt-4'>
									<button
										type='button'
										onClick={() => setRatingProduct(null)}
										className='flex-1 px-3 py-2 sm:px-4 sm:py-3 border-2 border-gray-200 text-gray-700 font-semibold rounded-lg sm:rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all text-xs sm:text-sm'
									>
										Cancelar
									</button>
									<button
										type='submit'
										disabled={isSubmitting || submittingRating}
										className='flex-1 px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-lg sm:rounded-xl hover:from-red-600 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center text-xs sm:text-sm transform hover:scale-105 shadow-md hover:shadow-lg disabled:transform-none'
									>
										{isSubmitting || submittingRating ? (
											<>
												<div className='animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white mr-2'></div>
												Enviando...
											</>
										) : (
											<>
												<Star className='w-3 h-3 sm:w-4 sm:h-4 mr-2' />
												Enviar calificación
											</>
										)}
									</button>
								</div>
							</form>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}
